import { TemplateRef } from '@angular/core';
import { PhxTextDecoration, PhxTextIntent } from '../text/phx-text.types';

/** Widths for table columns. */
export enum PHX_TABLE_COLUMN_WIDTHS {
  COMPACT = '64px',
  MEDIUM_COMPACT = '72px',
  EXTRA_SMALL = '80px',
  SMALL = '120px',
  MEDIUM_SMALL = '160px',
  MEDIUM = '200px',
  LARGE = '240px',
  EXTRA_LARGE = '320px',
  EXTRA_EXTRA_LARGE = '400px',
}

// ──────────────────────────────────────────────
// Sort
// ──────────────────────────────────────────────

/** Direction of sort applied to a column. `null` means unsorted. */
export type PhxSortDirection = 'asc' | 'desc' | null;

/** Emitted when the user clicks a sortable column header. */
export interface PhxSortChangeEvent {
  /** The `key` of the column being sorted. */
  key: string;
  /** The new sort direction. */
  direction: PhxSortDirection;
}

// ──────────────────────────────────────────────
// Pagination
// ──────────────────────────────────────────────

/** Emitted when the user navigates to a different page. */
export interface PhxPageChangeEvent {
  /** The new (1-based) page index. */
  page: number;
  /** Items-per-page at time of navigation. */
  perPage: number;
}

// ──────────────────────────────────────────────
// Column Config & Groups
// ──────────────────────────────────────────────

/**
 * Describes a single label inside a column header.
 * Used when a column displays multiple sortable items (e.g. "Driver ID / Name").
 */
export interface PhxTableHeaderConfig {
  /** Display text for this header item. */
  label?: string;

  /** Whether this specific label is sortable. Default `false`. */
  sortable?: boolean;

  /**
   * Sort key emitted in `PhxSortChangeEvent` when this label is clicked.
   * Falls back to the parent column `key` if omitted.
   */
  sortKey?: string;
}

/** Configuration for a single table column. */
export interface PhxTableColumnConfig {
  /** Unique key. */
  key: string;

  /**
   * Column header label(s).
   *
   * - **`PhxTableHeaderConfig`** — single label with its own `sortable` / `sortKey`.
   * - **`PhxTableHeaderConfig[]`** — multiple labels rendered in the same header
   *   cell (e.g. "Driver ID / Name"). Each label can independently be sortable
   *   with its own `sortKey`.
   */
  headerConfig?: PhxTableHeaderConfig | PhxTableHeaderConfig[];

  /**
   * Optional tooltip content for the header, shown on hover over the label.
   */
  headerTooltipContent?: string;

  /** Column width preset from `PHX_TABLE_COLUMN_WIDTHS` (px values). */
  width?: PHX_TABLE_COLUMN_WIDTHS;

  /** Horizontal text alignment for the column (header + body cells). */
  align?: PhxTableCellAlign;

  /** Whether this column is visible. Default `true`. Hidden columns are excluded from rendering. */
  visible?: boolean;
}

// ──────────────────────────────────────────────
// Table Data & Row Selection
// ──────────────────────────────────────────────

export interface PhxTableData {
  id: number;
  [key: string]: any;
}

export interface PhxRowSelectionChangeEvent<T extends PhxTableData = PhxTableData> {
  /** Currently selected rows. */
  selected: T[];
  /** Whether all rows are selected. */
  allSelected: boolean;
}

// ──────────────────────────────────────────────
// Cell Types & Intents
// ──────────────────────────────────────────────

/** Cell content horizontal alignment. */
export type PhxTableCellAlign = 'left' | 'center' | 'right';

/** Cell content vertical alignment. */
export type PhxTableCellVerticalAlign = 'top' | 'middle' | 'bottom';

/** Intent for cell highlight border color. */
export type PhxCellHighlightIntent = 'accent' | 'caution' | 'negative' | 'positive' | 'warning';

/** Type of media that can be either image or video. */
export type PhxTableCellMediaType = 'image' | 'video';

// ──────────────────────────────────────────────
// Cell Tag
// ──────────────────────────────────────────────

/**
 * Where the tag renders relative to the primary/secondary text in a
 * `phx-table-cell-tag`. The placement also determines the tag's size.
 */
export type PhxTableCellTagPlacement =
  | 'tag-only'
  | 'as-primary'
  | 'above-primary'
  | 'below-primary'
  | 'right-of-primary'
  | 'right-of-secondary';

// ──────────────────────────────────────────────
// Cell Action
// ──────────────────────────────────────────────

/** Visual intent of a data-driven action menu item. `negative` renders red text. */
export type PhxTableCellActionItemIntent = 'standard' | 'negative';

/**
 * A clickable menu item rendered inside a `phx-table-cell-action` overflow menu.
 * Provide via `phxMenuItems` as an alternative to projecting `<phx-menu-item>` content.
 */
export interface PhxTableCellActionMenuItem {
  /** Discriminant — always absent/false for a clickable item. */
  isDivider?: false;
  /** Stable identifier echoed back in the `phxMenuItemClick` output. */
  key: string;
  /** Visible menu item text. */
  label: string;
  /** Disables this individual item. */
  disabled?: boolean;
  /** Visual intent; `negative` renders the item in red. Defaults to `standard`. */
  intent?: PhxTableCellActionItemIntent;
}

/** A horizontal separator rendered between data-driven action menu items. */
export interface PhxTableCellActionDivider {
  /** Discriminant — always `true` for a divider. */
  isDivider: true;
}

/** A single entry in a data-driven action menu: either a clickable item or a divider. */
export type PhxTableCellActionItem = PhxTableCellActionMenuItem | PhxTableCellActionDivider;

// ──────────────────────────────────────────────
// Column Groups
// ──────────────────────────────────────────────

/** Table-level super-header configuration for a contiguous set of columns. */
export interface PhxTableColumnGroupConfig {
  /** Super-header text rendered above the grouped columns. */
  header: string;
  /** Column keys that belong to this group. Columns still render in `phxColumnsConfig` order. */
  columns: string[];
}

export interface PhxColumnGroupSegment {
  header: string;
  colspan: number;
  isUngrouped: boolean;
  startColIdx: number;
  endColIdx: number;
  /** Super-header row: draw left border on this segment. */
  borderLeft: boolean;
  /** Super-header row: draw right border on this segment. */
  borderRight: boolean;
}

export interface PhxColumnGroupData {
  hasGroups: boolean;
  segments: PhxColumnGroupSegment[];
  rightBorders: Set<number>;
}

// ──────────────────────────────────────────────
// Sticky Columns
// ──────────────────────────────────────────────

export interface PhxStickyColumnInfo {
  index: number;
  side: 'start' | 'end';
  offset: string;
  isEdge: boolean;
}

// ──────────────────────────────────────────────
// Cell Style
// ──────────────────────────────────────────────

export interface PhxCellStyleInfo {
  align: PhxTableCellAlign | undefined;
  stickyInfo: PhxStickyColumnInfo | undefined;
  groupBorderRight: boolean;
}

// ──────────────────────────────────────────────
// Column Customizer
// ──────────────────────────────────────────────

export interface PhxTableColumnCustomizerItem {
  key: string;
  label: string;
  visible: boolean;
}

/** Visual style knobs of a single text run (size is fixed per line). */
export interface PhxTableCellTextStyle {
  intent?: PhxTextIntent;
  emphasis?: boolean;
  italic?: boolean;
  decoration?: PhxTextDecoration;
  /** Default false. When true, a tooltip of the value shows only while truncated. */
  truncate?: boolean;
  /** When set, clamps the text to this many lines (multi-line ellipsis) instead of a single truncated line. */
  maxLines?: number;
}

/** A single text run within a line. */
export interface PhxTableCellStringSegment extends PhxTableCellTextStyle {
  value: string;
  tooltipContent?: string | TemplateRef<any>;
}

/** Accepted input for any of the primary/secondary/tertiary lines. */
export type PhxTableCellStringInput = string | string[] | PhxTableCellStringSegment | PhxTableCellStringSegment[];

export interface PhxTableIconStyle {
  intent?: PhxTextIntent;
  icon: string;
  size?: number;
  tooltipContent?: string | TemplateRef<any>;
}

export type PhxStringCellPrefixIcon =
  | string
  | [string, string]
  | PhxTableIconStyle
  | [PhxTableIconStyle, PhxTableIconStyle];

export type PhxTableCellMediaSize = 'default' | 'large';
